import React, { useEffect, useState } from "react";
import api, { API_BASE, getToken } from "@/lib/api";
import ResourcePage from "@/components/ResourcePage";
import { Badge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { QrCode, Download, Printer } from "lucide-react";

export default function Feedback() {
  const [branches, setBranches] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrBranch, setQrBranch] = useState("");
  const [qrSrc, setQrSrc] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data)); }, []);

  useEffect(() => {
    if (!qrOpen) return undefined;
    let cancelled = false;
    (async () => {
      const token = getToken();
      const url = `${API_BASE}/feedback-qr${qrBranch ? `?branch=${encodeURIComponent(qrBranch)}` : ""}`;
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setQrUrl(res.headers.get("X-QR-URL") || "");
      const blob = await res.blob();
      if (!cancelled) setQrSrc(URL.createObjectURL(blob));
    })();
    return () => { cancelled = true; setQrSrc(""); };
  }, [qrOpen, qrBranch]);

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setQrOpen(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="feedback-qr-button">
          <QrCode size={14} className="mr-1.5" /> Generate customer feedback QR
        </Button>
      </div>
      <ResourcePage
        endpoint="/feedback"
        testidPrefix="feedback"
        eyebrow="Stakeholders"
        title="Customer Feedback & Loyalty"
        subtitle="Capture complaints, suggestions, survey responses and ratings from customers. Public feedback (via scanned QR or /site/contact) flows here automatically."
        ctaLabel="Log feedback"
        fields={[
          { name: "customer_name", label: "Customer name" },
          { name: "customer_phone", label: "Phone" },
          { name: "branch", label: "Branch", type: "select", required: true, options: branches.map((b) => b.name) },
          { name: "type", label: "Type", type: "select", required: true,
            options: ["complaint", "suggestion", "survey", "rating"], default: "complaint" },
          { name: "subject", label: "Subject", required: true },
          { name: "message", label: "Message", type: "textarea", required: true },
          { name: "rating", label: "Rating (1-5)", type: "number" },
          { name: "status", label: "Status", type: "select", default: "open",
            options: ["open", "in_progress", "resolved", "closed"] },
        ]}
        columns={[
          { key: "subject", label: "Subject", render: (i) => (
            <div>
              <div className="font-medium text-gray-900">{i.subject}</div>
              <div className="text-xs text-gray-500 line-clamp-1 max-w-xs">{i.message}</div>
            </div>
          ) },
          { key: "type", label: "Type", render: (i) => <Badge tone={i.type === "complaint" ? "red" : i.type === "suggestion" ? "blue" : "emerald"}>{i.type}</Badge> },
          { key: "source", label: "Source", render: (i) => <Badge tone={i.source === "public_form" ? "amber" : "slate"}>{i.source === "public_form" ? "Scanned QR / Web" : "Staff entry"}</Badge> },
          { key: "customer_name", label: "Customer" },
          { key: "branch", label: "Branch" },
          { key: "rating", label: "Rating", render: (i) => i.rating ? "★".repeat(Number(i.rating)) : "—" },
          { key: "status", label: "Status", render: (i) => <Badge tone={i.status === "resolved" ? "emerald" : i.status === "open" ? "amber" : "blue"}>{i.status}</Badge> },
        ]}
        searchKeys={["customer_name", "subject", "message", "branch"]}
      />

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Customer Feedback QR</DialogTitle></DialogHeader>
          <div>
            <label className="text-xs font-semibold uppercase text-gray-700">Branch (optional)</label>
            <select className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={qrBranch} onChange={(e) => setQrBranch(e.target.value)}>
              <option value="">All branches (general)</option>
              {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center">
              {qrSrc ? <img src={qrSrc} alt="Feedback QR" className="h-56 w-56 mx-auto bg-white p-2 rounded-md" /> : <div className="h-56 flex items-center justify-center text-sm text-gray-500">Generating…</div>}
              <div className="text-xs text-emerald-900 mt-3 font-semibold">{qrBranch || "All branches"}</div>
              <div className="text-[10px] text-gray-500 mt-1 break-all px-2">{qrUrl}</div>
            </div>
            <p className="text-xs text-gray-600 mt-3">
              Print this QR and post it at the till, customer-service desk, exits or on receipts.
              Customers scan with their phone camera and submit feedback — no app or login needed.
            </p>
            <div className="flex gap-2 mt-3">
              <Button onClick={() => { const a = document.createElement("a"); a.href = qrSrc; a.download = `feedback-qr-${qrBranch || "all"}.png`; a.click(); }}
                className="flex-1 bg-emerald-900 hover:bg-emerald-950 text-white" disabled={!qrSrc}>
                <Download size={13} className="mr-1.5" /> Download PNG
              </Button>
              <Button onClick={() => window.open(qrSrc, "_blank")}
                variant="outline" className="flex-1 border-emerald-900 text-emerald-900" disabled={!qrSrc}>
                <Printer size={13} className="mr-1.5" /> Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
